<script setup lang="ts">
import StoriesBreadcrumb from '@/components/customer-stories/StoriesBreadcrumb.vue'
import StoryCard from '@/components/customer-stories/StoryCard.vue'
import { getLatestStories, stories } from '@/data/stories'
import { getStoryBySlug, type Story } from '@/data/stories'
import { getStoryFolderByCategory } from '@/data/stories'
import { easeOut, motion } from 'motion-v'

const route = useRoute()
const isPlaying = ref(false)

const slugFromUrl = route.path.split('/').pop()
const story = getStoryBySlug(slugFromUrl?.toString() || '')

const lastThreeStories = getLatestStories(stories, slugFromUrl, 3)

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const storyVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Customer Stories',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/customer-stories`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': story?.category,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories?category=${encodeURIComponent(story?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': story?.title,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories/${getStoryFolderByCategory(story?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories/${getStoryFolderByCategory(story?.category || '')}/${slugFromUrl}`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: story?.seo?.title,
  description: story?.seo?.description,
  ogTitle: story?.seo?.title,
  ogDescription: story?.seo?.description,
  ogImage: story?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4 md:shadow-2xl md:shadow-black/10"
    >
      <section class="3xl:px-0 mx-auto mb-8 px-4 lg:max-w-screen-xl">
        <StoriesBreadcrumb :story="story as Story" />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-900 lg:text-5xl"
        >
          {{ story?.title }}
        </motion.h1>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          SpotHopper helped Midtown Social improve online visibility across search and social, making it easier for new customers to discover the restaurant
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          <span v-html="story?.subtitle" />
        </motion.p>

        <div
          :variants="storyVariants"
          initial="initial"
          while-in-view="animate"
          class="relative my-6 flex flex-col justify-between gap-5 rounded-[40px] border border-gray-200 bg-white p-11 lg:flex-row xl:-ml-[3.5%] xl:w-[107%]"
        >
          <motion.p
            :variants="paragraphVariants"
            :custom="0"
            initial="initial"
            while-in-view="animate"
            class="absolute -top-1 left-11 pt-4 text-left text-slate-700"
          >
            Key Results
          </motion.p>
          <motion.div
            v-for="item in story?.highlight"
            :key="item.text"
            :variants="storyVariants"
            initial="initial"
            while-in-view="animate"
            class="flex w-full flex-col gap-y-2 rounded-2xl border border-gray-200 bg-[#f8f8f0] p-8 text-center"
          >
            <p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="text-4xl font-bold text-orange-500"
            >
              {{ item.number }}
            </p>
            <p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="text-slate-700"
            >
              {{ item.text }}
            </p>
          </motion.div>
        </div>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 text-left text-xl text-slate-700"
        >
          SpotHopper helped Midtown Social increase online visibility—making it easier for new customers to discover and choose the restaurant.
        </motion.p>
        <div class="mb-5 mt-8 flex flex-col items-stretch gap-4 md:flex-row">
          <motion.div
            :initial="{ opacity: 0, y: 50 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
            class="relative w-full overflow-hidden rounded-2xl drop-shadow-lg md:w-7/12"
          >
            <div
              v-if="!isPlaying"
              class="relative cursor-pointer"
              @click="isPlaying = true"
            >
              <img
                :src="story?.image"
                :alt="story?.title"
                class="w-full object-cover"
              >
              <div class="absolute inset-0 z-10 grid place-content-center">
                <div class="relative grid size-16 place-content-center before:absolute before:-inset-2 before:rounded-[50%] before:bg-gray-800/30 before:backdrop-blur-sm after:absolute after:inset-0 after:rounded-[50%] after:bg-gray-800/70 after:backdrop-blur-sm">
                  <UiIcon
                    name="play"
                    class="z-[1] text-2xl text-white"
                  />
                </div>
              </div>
              <div class="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
                <p class="text-md font-semibold text-white">
                  {{ story?.author?.split(',')[0] }}
                </p>
                <p class="text-md text-white/80">
                  {{ story?.author?.split(',').slice(1).join(',').trim() }}
                </p>
              </div>
            </div>
            <video
              v-else
              autoplay
              controls
              class="w-full"
              :src="story?.videoUrl"
            />
          </motion.div>

          <div class="flex w-full flex-col justify-between rounded-2xl border-4 border-[#FF711833] bg-white px-4 py-6 md:w-5/12">
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="relative w-full text-xl leading-relaxed text-slate-700"
            >
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pb-1"
              >
                <path
                  d="M33.7768 1.80041e-06C34.496 1.86329e-06 35 0.546003 35 1.1585C35 1.59425 34.7463 2.0615 34.1303 2.38875C31.0013 4.046 27.3788 9.78425 27.3788 13.3228C32.6568 13.1618 34.9493 17.8413 34.9493 20.7743C34.9493 24.2848 31.9077 28 27.4102 28C21.7385 28 18.9577 23.5427 18.9577 18.7127C18.9578 8.246 30.9173 1.55042e-06 33.7768 1.80041e-06ZM14.819 1.43069e-07C15.5365 2.05795e-07 16.0423 0.546002 16.0423 1.1585C16.0423 1.59425 15.7885 2.0615 15.1725 2.38875C12.0435 4.046 8.421 9.78425 8.421 13.3227C13.6972 13.1617 15.9915 17.8412 15.9915 20.7742C15.9915 24.2847 12.9482 28 8.4525 28C2.78075 28 -3.42503e-06 23.5427 -3.00278e-06 18.7127C-2.08775e-06 8.246 11.9578 -1.07069e-07 14.819 1.43069e-07Z"
                  fill="#FF7118"
                />
              </svg>
              <span class="">
                {{ story?.pullQuote }}</span>
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pt-1"
              >
                <path
                  d="M1.22325 28C0.504 28 0 27.454 0 26.8415C0 26.4057 0.25375 25.9385 0.86975 25.6112C3.99875 23.954 7.62125 18.2157 7.62125 14.6772C2.34325 14.8382 0.0507501 10.1587 0.0507501 7.22575C0.0507501 3.71525 3.09225 0 7.58975 0C13.2615 0 16.0423 4.45725 16.0423 9.28725C16.0423 19.754 4.08275 28 1.22325 28ZM20.181 28C19.4635 28 18.9578 27.454 18.9578 26.8415C18.9578 26.4057 19.2115 25.9385 19.8275 25.6112C22.9565 23.954 26.579 18.2157 26.579 14.6772C21.3028 14.8382 19.0085 10.1587 19.0085 7.22575C19.0085 3.71525 22.0518 0 26.5475 0C32.2193 0 35 4.45725 35 9.28725C35 19.754 23.0423 28 20.181 28Z"
                  fill="#FF7118"
                />
              </svg>
            </motion.p>
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="pt-4 text-left  text-slate-700"
            >
              <span class="text-lg font-bold text-orange-500 md:text-xl">{{ story?.author?.split(',')[0] }}</span> <br> {{ story?.author?.split(',').slice(1).join(',').trim() }}
            </motion.p>
          </div>
        </div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          The Challenge
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Midtown Social Restaurant & Bar is a high-energy, vibe dining destination in West Midtown Atlanta, built around great food, music, and experiences.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          But getting discovered was a challenge.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Like many independent restaurant owners, Devon Green was managing marketing across multiple channels while running a busy operation. Staying visible meant constantly posting on social media, responding to customer reviews, updating website content, and promoting new dishes and events.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          "Before SpotHopper, the challenge mainly came to responding to customer reviews, promoting dishes, and growing our business with the website," explains Devon.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Even with a strong concept, inconsistent marketing made it harder to stay top-of-mind and compete in a fast-growing area.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Changed
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          After implementing SpotHopper, Midtown Social became easier to find across the channels that drive dining decisions: Google, social media, and their website.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Search visibility improved, with thousands of monthly Google searches and a measurable increase in traffic from search.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Social media became a consistent driver of discovery, helping the restaurant reach new audiences and bring more visitors to the website.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          The website itself also became a stronger representation of the experience, using high-quality visuals and integrated tools to help customers understand what made Midtown Social unique before they ever visited.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          "They helped us build a website, with images and content that help us outshine our competitors," says Devon.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Instead of relying on fragmented efforts, Midtown Social established a consistent, high-quality digital presence that worked together across channels.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How SpotHopper Helped
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          SpotHopper created a connected marketing system that improved visibility across search, social, and the restaurant's website.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Content creation and distribution became faster and more consistent, allowing Midtown Social to regularly share updates, dishes, and promotions across platforms from a single place.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          "Integration with Instagram and Facebook allows me to upload a picture and send it to multiple platforms at one time," says Devon.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          The website was redesigned to better showcase the restaurant, with professional photos and videos that helped customers visualize the experience and make decisions more easily.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          At the same time, integrated tools made it easier to stay active and responsive online—keeping listings, content, and customer engagement up to date.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Together, these improvements made Midtown Social more visible where it matters most—helping more diners find the restaurant, understand the experience, and decide to visit.
        </motion.p>
      </section>
      <div class="mb-8 w-full bg-white pb-8 pt-8 md:rounded-[5rem]">
        <section class="mx-auto mb-8 px-2 pt-16 lg:max-w-screen-xl  2xl:px-0">
          <section
            v-if="lastThreeStories.length"
            class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0"
          >
            <motion.h2
              :variants="headlineVariants"
              initial="initial"
              while-in-view="animate"
              class="text-left text-2xl font-medium text-gray-900 lg:text-3xl xl:px-0"
            >
              More Customer Visibility Stories
            </motion.h2>
            <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 py-6 md:grid-cols-3">
              <motion.div
                v-for="(item, index) in lastThreeStories"
                :key="item.slug"
                :custom="index"
                :variants="storyVariants"
                initial="initial"
                while-in-view="animate"
              >
                <StoryCard v-bind="item" />
              </motion.div>
            </div>
          </section>
          <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
            <div
              class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20"
            >
              <div
                class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70"
              >
                <motion.img
                  :initial="{ opacity: 0, y: 50 }"
                  :while-in-view="{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: easeOut },
                  }"
                  :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                  class="aspect-video size-full object-cover object-center"
                  role="presentation"
                />
              </div>

              <div class="isolate grid place-items-center gap-5">
                <motion.h3
                  :variants="headlineVariants"
                  initial="initial"
                  while-in-view="animate"
                  class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
                >
                  Ready to take your restaurant marketing to the next level?
                </motion.h3>

                <UiButton
                  class="group w-full max-w-xs lg:max-w-max"
                  as-child
                >
                  <RouterLink :to="{ name: '/free-demo' }">
                    Book a Free Demo

                    <UiIcon
                      name="arrow-forward-rounded"
                      class="transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </RouterLink>
                </UiButton>
              </div>
            </div>
          </section>
        </section>
      </div>
      <LayoutFooter />
    </div>
  </div>
</template>
